import { useTranslation } from "next-i18next";
import { memo } from "react";

import { formatSubscriptionPrice } from "~lib/shop/calculatePrices";
import classnames from "~utils/classnames/classnames";

import LogoAmsterdamPas from "~public/images/logo-amsterdam-pas.svg";

import type { ShopCardContentLine, ShopCardPreviewProps } from "./types";

const EMPTY_CONTENT_ITEMS: Array<ShopCardContentLine> = [];

/**
 * Product card component that displays product information with optional addons
 */
const ShopCardPreview: React.FC<ShopCardPreviewProps> = ({
  product,
  contentItems = EMPTY_CONTENT_ITEMS,
  className,
}) => {
  const { t } = useTranslation();

  const gridSColpan = contentItems.length === 1 ? "col-span-2" : "col-auto";
  return (
    <article
      className={classnames(
        "border-grey-border flex min-h-64 flex-col gap-4 rounded-3xl border border-solid bg-black p-8 text-white",
        className,
      )}
    >
      {/* Card header with product info */}
      <header className="flex w-full items-start justify-between">
        <div className="flex flex-1 items-center justify-between">
          {/* Logo */}
          <div className="relative overflow-hidden">
            <LogoAmsterdamPas className="w-30 h-10" />
          </div>

          {/* Product title and price */}
          <div className="flex flex-col items-end text-white">
            <h3 className="!text-header-5">{product.title}</h3>
            <p className="text-body">
              {formatSubscriptionPrice(t, product.price, product.frequency)}
            </p>
          </div>
        </div>
      </header>

      <hr className="bg-matte-black h-px border-0" aria-hidden="true" />

      {/* Card content grid */}
      <div className="grid w-full grid-cols-3 gap-4">
        {contentItems.map(({ id, label, content }) => (
          <div key={id} className={classnames("flex flex-col", gridSColpan)}>
            {label && <span className="text-caption">{label}</span>}
            <div className="text-body-large line-clamp-3">{content}</div>
          </div>
        ))}
      </div>
    </article>
  );
};

export default memo(ShopCardPreview);
